import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, from, map, Observable, throwError } from 'rxjs';
import { IRoleOverrideService, IUserService } from '../interfaces';
import { CacheService } from '../../misc';
import { IUserHttpService } from '../../http';
import { Country, Role, Roles, UserModel, UserTransformer, Countries } from '../../../models';

enum UserCacheKeys {
  ME = 'USER_ME',
}

@Injectable()
export class UserService implements IUserService {
  public userCountries$: BehaviorSubject<Country[]> = new BehaviorSubject([] as Country[]);
  private primaryRole?: Role;
  private isDummyUser = false;

  constructor(
    private userHttpService: IUserHttpService,
    private cacheService: CacheService,
    private roleOverrideService: IRoleOverrideService,
  ) {
    const loadMe = async (): Promise<UserModel> => {
      let userBack;
      try {
        userBack = await this.userHttpService.getMe();
        this.isDummyUser = false;
      } catch (error) {
        console.error('Error fetching user data, fallback to dummy user for debugging:', error);

        // Check if this is a real authentication error (network, CORS, 401, etc.)
        // In that case, we should rethrow the error instead of using dummy user
        if (this.isRealAuthError(error)) {
          this.isDummyUser = false;
          throw error;
        }

        this.isDummyUser = true;
      }

      if (!userBack) {
        // Create a dummy user for debugging if no user data is returned.
        userBack = {
          nom: 'Dummy User',
          role: Roles.ROLE_HOME_ACCESS_ONLY,
          countries: [Countries.FR],
          version: 'debug',
        };
        this.isDummyUser = true;
      }

      const user = UserTransformer.parse(userBack);
      this.primaryRole = user.role;
      this.userCountries$.next(user.countries);
      return user;
    };

    this.cacheService.register(UserCacheKeys.ME, loadMe);
  }

  /**
   * Determines if an error is a real authentication error that should prevent login
   * rather than falling back to dummy user
   */
  private isRealAuthError(error: unknown): boolean {
    // Network errors (CORS, timeout, etc.)
    if (error && typeof error === 'object' && 'status' in error) {
      const httpError = error as { status: number; statusText?: string };
      // Status 0 usually indicates network/CORS issues
      // Status 401 indicates unauthorized
      // Status 403 indicates forbidden
      return httpError.status === 0 || httpError.status === 401 || httpError.status === 403;
    }

    // Other network-related errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return true;
    }

    return false;
  }

  /**
   * Checks if the current user is a dummy user (fallback user)
   */
  isDummyUserActive(): boolean {
    return this.isDummyUser;
  }

  /**
   * Forces a refresh of user data without dummy fallback
   */
  async refreshUserData(): Promise<UserModel> {
    this.cacheService.clear(UserCacheKeys.ME);
    this.isDummyUser = false;

    try {
      const userBack = await this.userHttpService.getMe();
      const user = UserTransformer.parse(userBack);
      this.primaryRole = user.role;
      this.userCountries$.next(user.countries);
      return user;
    } catch (error) {
      this.isDummyUser = false;
      throw error;
    }
  }

  me(): Observable<UserModel> {
    try {
      const user$ = from(this.cacheService.get<Promise<UserModel>>(UserCacheKeys.ME));
      const overriddenRole$ = this.roleOverrideService.getOverriddenRole$();

      return combineLatest([user$, overriddenRole$]).pipe(
        map(([user, overriddenRole]) => {
          return { ...user, role: overriddenRole && user?.role === Roles.ROLE_ADMIN ? overriddenRole : (this.primaryRole as Role) };
        }),
      );
    } catch (e) {
      this.cacheService.clear(UserCacheKeys.ME);
      throw e;
    }
  }
}
